/**
 * Features Grid Component for Puck Editor
 * 
 * Grid fitur/keunggulan - untuk menampilkan USP toko
 */

import type { ReactNode } from 'react'
import type { ComponentConfig } from '@measured/puck'

export interface FeaturesGridProps {
  features: Array<{
    icon: string
    title: string
    description: string
  }>
  columns: 2 | 3 | 4
  variant: 'cards' | 'simple' | 'icons-top'
}

// Common icons for UMKM
const IconMap: Record<string, ReactNode> = {
  truck: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  ),
  shield: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  star: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  ),
  clock: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  check: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  heart: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ),
  money: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  phone: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  ),
}

export function FeaturesGrid({ features, columns, variant }: FeaturesGridProps) {
  const gridCols = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }

  if (variant === 'cards') {
    return (
      <div className={`grid ${gridCols[columns]} gap-6`}>
        {features.map((feature, index) => (
          <div
            key={`${index}-${feature.icon}-${feature.title}`}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4">
              {IconMap[feature.icon] || IconMap.check}
            </div>
            <h3 className="font-semibold text-lg text-gray-900 mb-2">
              {feature.title}
            </h3>
            <p className="text-gray-600">{feature.description}</p>
          </div>
        ))}
      </div>
    )
  }

  if (variant === 'icons-top') {
    return (
      <div className={`grid ${gridCols[columns]} gap-8`}>
        {features.map((feature, index) => (
          <div key={`${index}-${feature.icon}-${feature.title}`} className="text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 mx-auto mb-4">
              {IconMap[feature.icon] || IconMap.check}
            </div>
            <h3 className="font-semibold text-lg text-gray-900 mb-2">
              {feature.title}
            </h3>
            <p className="text-gray-600">{feature.description}</p>
          </div>
        ))}
      </div>
    )
  }

  // Simple variant
  return (
    <div className={`grid ${gridCols[columns]} gap-6`}>
      {features.map((feature, index) => (
        <div key={`${index}-${feature.icon}-${feature.title}`} className="flex gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
            {IconMap[feature.icon] || IconMap.check}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">
              {feature.title}
            </h3>
            <p className="text-sm text-gray-600">{feature.description}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

export const FeaturesGridConfig: ComponentConfig<FeaturesGridProps> = {
  label: 'Grid Fitur',
  defaultProps: {
    features: [
      {
        icon: 'truck',
        title: 'Pengiriman Cepat',
        description: 'Pesanan dikirim dalam 1-2 hari kerja',
      },
      {
        icon: 'shield',
        title: 'Garansi Produk',
        description: 'Garansi uang kembali 30 hari',
      },
      {
        icon: 'phone',
        title: 'Layanan 24/7',
        description: 'Siap membantu kapan saja',
      },
    ],
    columns: 3,
    variant: 'cards',
  },
  fields: {
    features: {
      type: 'array',
      label: 'Daftar Fitur',
      getItemSummary: (feature) => feature?.title || 'Fitur',
      arrayFields: {
        icon: {
          type: 'select',
          label: 'Ikon',
          options: [
            { label: 'Truk (Pengiriman)', value: 'truck' },
            { label: 'Perisai (Keamanan)', value: 'shield' },
            { label: 'Bintang', value: 'star' },
            { label: 'Jam', value: 'clock' },
            { label: 'Centang', value: 'check' },
            { label: 'Hati', value: 'heart' },
            { label: 'Uang', value: 'money' },
            { label: 'Telepon', value: 'phone' },
          ],
        },
        title: {
          type: 'text',
          label: 'Judul',
        },
        description: {
          type: 'textarea',
          label: 'Deskripsi',
        },
      },
      defaultItemProps: {
        icon: 'check',
        title: 'Fitur Baru',
        description: 'Deskripsi fitur',
      },
    },
    columns: {
      type: 'select',
      label: 'Jumlah Kolom',
      options: [
        { label: '2 Kolom', value: 2 },
        { label: '3 Kolom', value: 3 },
        { label: '4 Kolom', value: 4 },
      ],
    },
    variant: {
      type: 'select',
      label: 'Gaya',
      options: [
        { label: 'Cards', value: 'cards' },
        { label: 'Simple', value: 'simple' },
        { label: 'Icons Top', value: 'icons-top' },
      ],
    },
  },
  render: FeaturesGrid,
}
